/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { getEstimateItems, correctDecimalPlaces } from "@Actions/estimation";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { useParams } from "react-router-dom";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { useSelector } from "react-redux";
import type { RootState } from "src/reducers";
import SlickGrid1, { type GridRowBase } from "@CubeComponents/html/SlickGrid1";
import { Formatters, type SlickgridReactInstance } from "slickgrid-react";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import Text from "@CubeComponents/html/Text/Text";
import type { EstimateCurrency, EstimateData, GetEstimateItemsResponse } from "@ServerTypes/estimates/estimates.schema";

type CurrencyData = EstimateCurrency;

type EstimateItemSummary = NonNullable<GetEstimateItemsResponse["result"]>[number];

type SummaryTableProps = {
  estimateId: string;
  versionId?: string;
  currencyData: CurrencyData;
};

interface SummaryRow extends GridRowBase {
  id: string;
  description: string;
  dsrAmount: number;
  dsrAmountFactor: number;
  nonDsrAmount: number;
  nonDsrAmountFactor: number;
  parentId: string | null;
  serial?: number;
  hasChild: boolean;
  treeLevel: number;
  rate?: number;
  createdDate: string;
  __hasChildren: boolean;
  __collapsed: boolean;
}

function SummaryTable(props: SummaryTableProps) {
  const { t, i18n } = useTranslation();
  const params = useParams<{ versionId?: string }>();

  const [levelValue, setLevelValue] = useState(0);
  const [factorValue, setFactorValue] = useState(1);
  const [summaryData, setSummaryData] = useState<EstimateItemSummary[]>([]);
  const [dataset, setDataset] = useState<SummaryRow[]>([]);
  const gridRef = useRef<SlickgridReactInstance | null>(null);
  const appliedFactorRef = useRef(1);
  const appliedLevelRef = useRef(0);
  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateData;

  const isPrevEstimateVersionOpened = !!params.versionId;
  const currencySymbol = props.currencyData.selectedSymbol.name;

  const amountFormatter = useCallback(
    (_row: number, _cell: number, value: unknown, colDef: any, dataContext: SummaryRow) => {
      const formatted =
        typeof value === "number" && value !== 0
          ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : "0";
      const isGrandTotal = dataContext?.id === "__grand_total__";
      const weight = isGrandTotal ? "font-weight:bold;" : "";
      const row = dataContext?.description || "";
      const col = colDef?.name || "";
      const tip = `Row: ${row}&#10;Column: ${col}&#10;Value: ${formatted}`;
      return `<div style="${weight}width:100%;height:100%" title="${tip.replace(/"/g, "&quot;")}">${formatted}</div>`;
    },
    [],
  );

  const columnDefinitions = useMemo(() => {
    return [
      {
        id: "description",
        name: t("Items"),
        field: "description" as const,
        width: 400,
        minWidth: 250,
        sortable: false,
        resizable: true,
        formatter: Formatters.tree,
      },
      {
        id: "dsrAmount",
        name: `${t("Amount")} (${currencySymbol})`,
        field: "dsrAmount" as const,
        width: 200,
        minWidth: 120,
        sortable: false,
        resizable: true,
        columnGroup: t("Amounts"),
        formatter: amountFormatter,
      },
      {
        id: "dsrAmountFactor",
        name: `${t("Amount / Factor")} (${currencySymbol})`,
        field: "dsrAmountFactor" as const,
        width: 200,
        minWidth: 120,
        sortable: false,
        resizable: true,
        columnGroup: t("Amounts"),
        formatter: amountFormatter,
      },
      {
        id: "nonDsrAmount",
        name: `${t("Market rate Item Amount")} (${currencySymbol})`,
        field: "nonDsrAmount" as const,
        width: 250,
        minWidth: 140,
        sortable: false,
        resizable: true,
        columnGroup: t("Amounts"),
        formatter: amountFormatter,
      },
      {
        id: "nonDsrAmountFactor",
        name: `${t("Market rate Item Amount / Factor")} (${currencySymbol})`,
        field: "nonDsrAmountFactor" as const,
        width: 250,
        minWidth: 140,
        sortable: false,
        resizable: true,
        columnGroup: t("Amounts"),
        formatter: amountFormatter,
      },
    ] as any;
  }, [currencySymbol, amountFormatter]);

  const getData = useCallback(async () => {
    const res = await getEstimateItems({
      value: {
        params: {
          estimateId: props.estimateId,
          versionId: props.versionId,
          getAllItems: true,
          modifyOutputForSummary: true,
        },
      },
    });
    setSummaryData(res.data.result!);
  }, [props.estimateId, props.versionId]);

  useEffect(() => {
    getData();
  }, [getData]);

  const summaryDecimalPlaces = estimateData.settings!.decimalPlaces;
  const prepareTreeData = useCallback(
    (data: EstimateItemSummary[], factor: number, maxLevel: number): SummaryRow[] => {
      const rowMap = new Map<string, SummaryRow>();
      for (const item of data) {
        const id = item.itemId;
        const amountValue = item.amount ?? 0;
        const additionalValue = item.additionalAmount ?? 0;
        const ownAmount = item.hasChild
          ? correctDecimalPlaces({
              value: additionalValue,
              decimals: estimateData.settings!.decimalPlaces!.amount,
            }) || 0
          : correctDecimalPlaces({
              value: amountValue + additionalValue,
              decimals: estimateData.settings!.decimalPlaces!.amount,
            }) || 0;

        const desc = item.code ? `${item.code} - ${item.description}` : (item.description ?? "");

        rowMap.set(id, {
          id,
          description: desc,
          dsrAmount: ownAmount && item.code ? ownAmount : 0,
          dsrAmountFactor: ownAmount && item.code ? Number((ownAmount / factor).toFixed(2)) : 0,
          nonDsrAmount: ownAmount && !item.code ? ownAmount : 0,
          nonDsrAmountFactor: ownAmount && !item.code ? Number((ownAmount / factor).toFixed(2)) : 0,
          parentId: item.parentId ? item.parentId : null,
          serial: item.serial,
          hasChild: !!item.hasChild && item.itemLevel! < maxLevel,
          treeLevel: item.itemLevel!,
          rate: item.rate as number | undefined,
          createdDate: item.createdDate as string,
          __hasChildren: !!item.hasChild && item.itemLevel! < maxLevel,
          __collapsed: false,
        });
      }

      for (const item of data) {
        if (item.hasChild || !item.parentsList) continue;
        const id = item.itemId;
        const leafRow = rowMap.get(id);
        if (!leafRow) continue;

        for (const ancestor of item.parentsList) {
          if (ancestor.itemId === "0") continue; // skip root sentinel
          const ancestorKey = ancestor.itemId;
          const ancestorRow = rowMap.get(ancestorKey);
          if (ancestorRow) {
            ancestorRow.dsrAmount += leafRow.dsrAmount;
            ancestorRow.dsrAmountFactor += leafRow.dsrAmountFactor;
            ancestorRow.nonDsrAmount += leafRow.nonDsrAmount;
            ancestorRow.nonDsrAmountFactor += leafRow.nonDsrAmountFactor;
          }
        }
      }

      // Round parent amounts after aggregation
      for (const row of rowMap.values()) {
        if (row.hasChild) {
          row.dsrAmount = Number(row.dsrAmount.toFixed(2));
          row.dsrAmountFactor = Number(row.dsrAmountFactor.toFixed(2));
          row.nonDsrAmount = Number(row.nonDsrAmount.toFixed(2));
          row.nonDsrAmountFactor = Number(row.nonDsrAmountFactor.toFixed(2));
        }
      }

      const rows: SummaryRow[] = [];
      for (const item of data) {
        if (item.itemLevel! > maxLevel) continue;
        const id = item.itemId;
        const row = rowMap.get(id);
        if (row) rows.push(row);
      }

      // Compute grand total from root-level items only (to avoid double-counting)
      let grandDsr = 0;
      let grandDsrFactor = 0;
      let grandNonDsr = 0;
      let grandNonDsrFactor = 0;
      for (const row of rows) {
        if (!row.parentId) {
          grandDsr += row.dsrAmount;
          grandDsrFactor += row.dsrAmountFactor;
          grandNonDsr += row.nonDsrAmount;
          grandNonDsrFactor += row.nonDsrAmountFactor;
        }
      }

      rows.push({
        id: "__grand_total__",
        description: t("Grand Total"),
        dsrAmount: Number(grandDsr.toFixed(2)),
        dsrAmountFactor: Number(grandDsrFactor.toFixed(2)),
        nonDsrAmount: Number(grandNonDsr.toFixed(2)),
        nonDsrAmountFactor: Number(grandNonDsrFactor.toFixed(2)),
        parentId: null,
        hasChild: false,
        treeLevel: 0,
        createdDate: new Date().toISOString(),
        __hasChildren: false,
        __collapsed: false,
      });

      return rows;
    },

    [summaryDecimalPlaces],
  );

  useEffect(() => {
    if (summaryData.length > 0) {
      const rows = prepareTreeData(summaryData, appliedFactorRef.current, appliedLevelRef.current);
      setDataset(rows);
    }
  }, [summaryData, prepareTreeData]);

  const handleShowClick = () => {
    appliedLevelRef.current = levelValue;
    if (summaryData.length > 0) {
      const rows = prepareTreeData(summaryData, appliedFactorRef.current, levelValue);
      setDataset(rows);
    }
  };

  const handleFactorSave = () => {
    appliedFactorRef.current = factorValue;
    if (summaryData.length > 0) {
      const rows = prepareTreeData(summaryData, factorValue, appliedLevelRef.current);
      setDataset(rows);
    }
  };

  const inputNum = () => (
    <div className="flex w-full items-center p-6 gap-10">
      <div className="flex gap-3">
        <div className="flex gap-2 items-center">
          <Text>{t("Level of nesting (between {{min, number}} and {{max, number}})", { min: 0, max: 5 })}</Text>
          <div>:</div>
        </div>
        <NumericTextBoxComponent
          min={0}
          max={5}
          defaultValue={0}
          width="6.25rem"
          placeholder={new Intl.NumberFormat(i18n.language).format(0)}
          onChange={(e) => setLevelValue(Number(e.target.value))}
        />
        <ButtonComponent buttonType="gradientButton" onClick={handleShowClick}>
          {t("Show")}
        </ButtonComponent>
      </div>
      <div className="flex gap-3">
        <div className="flex gap-2 items-center">
          <div>{t("Factor")}</div>
          <div>:</div>
        </div>
        <TextFieldComponent
          width="6.25rem"
          type="number"
          placeholder={t("Enter factor Value")}
          value={String(factorValue)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFactorValue(Number(e.target.value))}
          disabled={isPrevEstimateVersionOpened}
        />
        <ButtonComponent
          buttonType="gradientButton"
          onClick={handleFactorSave}
          disabled={isPrevEstimateVersionOpened || factorValue <= 0}
        >
          {t("Save")}
        </ButtonComponent>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {inputNum()}
      <div className="flex overflow-auto w-full h-full">
        <SlickGrid1<SummaryRow>
          ref={gridRef as any}
          initiallyExpanded
          columnDefinitions={columnDefinitions}
          dataset={dataset}
          treeGrid
          disableCustomSorting
          disableChildDataFetch
          hasChildMapping="hasChild"
          parentIdMapping="parentId"
          levelPropName="treeLevel"
          initialSortColumnId="createdDate"
          initialSortDirection="ASC"
          allowPagination={false}
          disableCheckBoxSelection
          showPageCount={false}
          gridOptions={{
            showPreHeaderPanel: true,
            preHeaderPanelHeight: 30,
            enableSorting: false,
            enableColumnReorder: false,
            enableAutoResize: true,
            enableFiltering: true,
            rowHeight: 36,
            headerRowHeight: 40,
          }}
        />
      </div>
    </div>
  );
}

export default SummaryTable;
